import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import LogoMark from "@/components/LogoMark";
import {
  Mail, Lock, Eye, EyeOff, ArrowLeft, Layers, ShieldCheck,
  Sparkles, TrendingUp, Users, Award, CheckCircle2,
  HelpCircle, Globe, Star,
} from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [remember, setRemember] = useState(false);
  const [loading, setLoading] = useState(false);

  if (user) {
    return <Navigate to={user.role === "super_admin" ? "/schools" : "/dashboard"} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success("مرحبًا بعودتك");
      nav(res.user?.role === "super_admin" ? "/schools" : "/dashboard");
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen flex flex-col relative overflow-hidden" style={{ background: "#F6F8F5" }} dir="rtl" data-testid="login-page">
      {/* ═══════════════ TOP BAR ═══════════════ */}
      <header className="relative z-20 flex items-center justify-between px-8 py-5 bg-white/80 backdrop-blur-xl border-b border-slate-100">
        <div className="flex items-center gap-3">
          <div
            className="h-11 w-11 rounded-2xl flex items-center justify-center shrink-0 relative"
            style={{
              background: "linear-gradient(135deg, #0E7469 0%, #14B8A6 100%)",
              boxShadow: "0 6px 20px -4px rgba(14, 116, 105, 0.45), 0 0 0 1px rgba(255,255,255,0.14) inset",
            }}
          >
            <Layers className="h-5 w-5 text-white relative z-10" strokeWidth={2} />
            <div className="absolute -inset-1 rounded-2xl bg-[#14B8A6] opacity-30 blur-md -z-10" />
          </div>
          <div>
            <div className="font-display text-[17px] font-bold text-slate-900 leading-tight tracking-tight">
              المسار المصري
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-1.5">
              <span className="h-1 w-1 rounded-full bg-emerald-500" />
              نظام إدارة الدرجات والشهادات
            </div>
          </div>
        </div>
        <div className="hidden md:flex items-center gap-2">
          <button className="flex items-center gap-1.5 h-9 px-3 rounded-lg text-[12.5px] text-slate-600 hover:bg-slate-50 transition-colors">
            <HelpCircle className="h-3.5 w-3.5" strokeWidth={1.75} />
            مساعدة
          </button>
          <button className="flex items-center gap-1.5 h-9 px-3 rounded-lg text-[12.5px] text-slate-700 hover:bg-slate-50 border border-slate-200 transition-colors">
            <Globe className="h-3.5 w-3.5" strokeWidth={1.75} />
            العربية
          </button>
        </div>
      </header>

      {/* ═══════════════ MAIN SPLIT ═══════════════ */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[minmax(0,520px)_1fr] max-w-[1600px] w-full mx-auto">

        {/* ─────── FORM PANEL (right in RTL) ─────── */}
        <div className="relative flex items-center justify-center px-8 lg:px-14 py-10">
          <div className="w-full max-w-md">

            {/* Signature badge */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-6"
                 style={{
                   background: "linear-gradient(135deg, rgba(14, 116, 105, 0.08), rgba(218, 165, 32, 0.06))",
                   border: "1px solid rgba(14, 116, 105, 0.14)",
                 }}>
              <Sparkles className="h-3.5 w-3.5" style={{ color: "#0E7469" }} strokeWidth={2} />
              <span className="text-[11.5px] font-semibold text-slate-700">النسخة الاحترافية 2026</span>
            </div>

            {/* Greeting */}
            <div className="mb-8">
              <h1 className="font-display text-[36px] font-bold text-slate-900 tracking-tight leading-[1.1]">
                مرحبا بعودتك
                <span className="ms-2 inline-block animate-[wave_1.5s_ease-in-out_infinite_alternate]" style={{ transformOrigin: "70% 70%" }}>👋</span>
              </h1>
              <p className="mt-3 text-[14px] text-slate-500 leading-relaxed">
                سجّل الدخول للوصول إلى منصّة إدارة درجات وشهادات مدرستك.
              </p>
            </div>

            {/* Form */}
            <form onSubmit={submit} className="space-y-4" data-testid="login-form">
              <div>
                <label className="block text-[12.5px] font-semibold text-slate-700 mb-2">
                  البريد الإلكتروني
                </label>
                <div className="relative group">
                  <Mail className="absolute end-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 group-focus-within:text-[#0E7469] transition-colors" strokeWidth={1.75} />
                  <input
                    type="email" required value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    dir="ltr"
                    className="w-full h-12 rounded-xl border-[1.5px] border-slate-200 bg-white text-[14px] px-4 pe-12 outline-none transition-all focus:border-[#0E7469] shadow-[0_1px_2px_rgba(6,41,36,0.02)]"
                    placeholder="example@school.eg"
                    data-testid="login-email"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[12.5px] font-semibold text-slate-700 mb-2">
                  كلمة المرور
                </label>
                <div className="relative group">
                  <Lock className="absolute end-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 group-focus-within:text-[#0E7469] transition-colors" strokeWidth={1.75} />
                  <button
                    type="button"
                    onClick={() => setShowPass(!showPass)}
                    className="absolute start-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 transition-colors"
                    tabIndex={-1}
                  >
                    {showPass
                      ? <EyeOff className="h-4 w-4" strokeWidth={1.75} />
                      : <Eye className="h-4 w-4" strokeWidth={1.75} />}
                  </button>
                  <input
                    type={showPass ? "text" : "password"} required value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    dir="ltr"
                    className="w-full h-12 rounded-xl border-[1.5px] border-slate-200 bg-white text-[14px] px-4 ps-12 pe-12 outline-none transition-all focus:border-[#0E7469] shadow-[0_1px_2px_rgba(6,41,36,0.02)]"
                    placeholder="••••••••"
                    data-testid="login-password"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between text-[12.5px] pt-1">
                <label className="flex items-center gap-2 cursor-pointer text-slate-600 select-none">
                  <input
                    type="checkbox" checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                    className="h-3.5 w-3.5 rounded border-slate-300 accent-[#0E7469]"
                  />
                  <span>تذكرني لمدة 30 يومًا</span>
                </label>
                <a href="#" className="text-[#0E7469] hover:text-[#083F38] font-semibold transition-colors">
                  نسيت كلمة المرور؟
                </a>
              </div>

              <button
                type="submit" disabled={loading}
                className="w-full h-12 rounded-xl text-[14px] font-semibold text-white flex items-center justify-center gap-2 transition-all disabled:opacity-70 relative overflow-hidden group mt-2"
                style={{
                  background: "linear-gradient(135deg, #0E7469 0%, #083F38 100%)",
                  boxShadow: "0 8px 24px -6px rgba(14, 116, 105, 0.45), 0 0 0 1px rgba(255,255,255,0.14) inset",
                }}
                data-testid="login-submit"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                <span className="relative">{loading ? "جارٍ الدخول…" : "تسجيل الدخول"}</span>
                {!loading && <ArrowLeft className="h-4 w-4 relative" strokeWidth={2} />}
              </button>
            </form>

            {/* Footer note */}
            <div className="mt-6 text-center text-[12px]">
              <span className="text-slate-500">ليس لديك حساب؟ </span>
              <a href="#" className="text-[#0E7469] font-bold hover:text-[#083F38] transition-colors">
                تواصل مع مدير المدرسة
              </a>
            </div>
          </div>
        </div>

        {/* ─────── HERO PANEL (left in RTL) ─────── */}
        <div className="hidden lg:flex relative overflow-hidden">
          {/* Mesh gradient */}
          <div className="absolute inset-0 mesh-nile" />
          {/* Fine grid */}
          <div className="absolute inset-0 grid-fine opacity-100" />
          {/* Grain overlay */}
          <div className="absolute inset-0 grain" />

          {/* Ambient glow orbs */}
          <div className="glow-orb" style={{ width: 400, height: 400, top: "-100px", left: "-100px", background: "#14B8A6" }} />
          <div className="glow-orb" style={{ width: 500, height: 500, bottom: "-150px", right: "-100px", background: "#DAA520", animationDelay: "3s" }} />
          <div className="glow-orb" style={{ width: 300, height: 300, top: "40%", right: "20%", background: "#0E7469", animationDelay: "6s" }} />

          {/* Content */}
          <div className="relative z-10 flex flex-col justify-between p-14 xl:p-16 w-full text-white">

            {/* TOP: Trust badge */}
            <div className="flex items-center justify-between">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full card-glass">
                <ShieldCheck className="h-3.5 w-3.5" style={{ color: "#F4D06F" }} strokeWidth={2} />
                <span className="text-[11.5px] font-semibold" style={{ color: "#F4D06F" }}>موثّق · متعدد المدارس · JWT</span>
              </div>
              <div className="flex items-center gap-1 text-[11px] text-white/60">
                <Star className="h-3 w-3 fill-current" style={{ color: "#F4D06F" }} />
                <Star className="h-3 w-3 fill-current" style={{ color: "#F4D06F" }} />
                <Star className="h-3 w-3 fill-current" style={{ color: "#F4D06F" }} />
                <Star className="h-3 w-3 fill-current" style={{ color: "#F4D06F" }} />
                <Star className="h-3 w-3 fill-current" style={{ color: "#F4D06F" }} />
                <span className="ms-1.5">4.9/5</span>
              </div>
            </div>

            {/* CENTER: Headline + floating preview */}
            <div className="grid grid-cols-1 xl:grid-cols-[1fr_auto] items-center gap-10">
              <div className="max-w-lg">
                <h2 className="font-display text-[38px] xl:text-[44px] font-bold leading-[1.15] tracking-tight">
                  إدارة كاملة
                  <br />
                  لدرجات وشهادات
                  <br />
                  <span style={{ color: "#F4D06F" }}>مدارس المسار المصري.</span>
                </h2>
                <div className="mt-5 hairline-gold w-24" />
                <p className="mt-5 text-[14.5px] text-white/75 leading-[1.9] max-w-md">
                  منظومة تعليمية متكاملة تجمع بين معادلات النسخة المكتبية الأصلية،
                  وتحليلات ذكية، وشهادات PDF فورية، بواجهة عربية أنيقة.
                </p>

                {/* Highlight bullets */}
                <div className="mt-7 space-y-2.5">
                  <BulletPoint text="100% مطابقة معادلات الدرجات الأصلية" />
                  <BulletPoint text="شهادات PDF فورية للفرد والفصل كاملاً" />
                  <BulletPoint text="متعدد المدارس مع عزل بيانات كامل" />
                  <BulletPoint text="استخراج تلقائي لبيانات الرقم القومي" />
                </div>
              </div>

              {/* Floating dashboard preview */}
              <div className="hidden xl:block relative">
                <DashboardPreview />
                {/* Floating stat badge — top */}
                <div
                  className="absolute -top-4 -start-8 card-glass px-3.5 py-2.5 rounded-xl z-20"
                  style={{ animation: "float 8s ease-in-out infinite" }}
                >
                  <div className="flex items-center gap-2">
                    <div className="h-8 w-8 rounded-lg icon-chip-hero flex items-center justify-center">
                      <TrendingUp className="h-3.5 w-3.5" strokeWidth={2} />
                    </div>
                    <div>
                      <div className="text-[10px] text-white/60 font-medium">نسبة النجاح</div>
                      <div className="font-display text-[15px] font-bold text-white tabular-nums">96.4%</div>
                    </div>
                  </div>
                </div>
                {/* Floating stat badge — bottom */}
                <div
                  className="absolute -bottom-4 -end-6 card-glass px-3.5 py-2.5 rounded-xl z-20"
                  style={{ animation: "float 9s ease-in-out infinite", animationDelay: "2s" }}
                >
                  <div className="flex items-center gap-2">
                    <div className="h-8 w-8 rounded-lg icon-chip-hero flex items-center justify-center">
                      <Award className="h-3.5 w-3.5" strokeWidth={2} />
                    </div>
                    <div>
                      <div className="text-[10px] text-white/60 font-medium">شهادات صادرة</div>
                      <div className="font-display text-[15px] font-bold text-white tabular-nums">2,847</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* BOTTOM: metrics strip */}
            <div>
              <div className="grid grid-cols-4 gap-6 mb-8">
                <BigStat value="100%" label="مطابقة الحسابات" />
                <BigStat value="RTL" label="عربية أصيلة" />
                <BigStat value="PDF" label="شهادات جاهزة" />
                <BigStat value="24/7" label="دعم فني" />
              </div>
              <div className="flex items-center justify-between text-[11px] text-white/50 border-t border-white/10 pt-6">
                <span className="flex items-center gap-1.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.7)]" />
                  الأنظمة تعمل بكفاءة
                </span>
                <span>© {new Date().getFullYear()} · الإصدار 1.3.0</span>
              </div>
              <div className="flex items-center justify-center gap-2 mt-4">
                <LogoMark size={22} />
                <span className="text-[12px] font-semibold text-white/80">KamelCloud <span className="text-white/50 font-normal">for Programming</span></span>
              </div>
              <div className="text-center text-[13.5px] font-semibold mt-2" style={{ color: "#F4D06F" }}>
                برمجة وتصميم الأستاذ/ محمد كامل ربيع — جوال / 000966590939015
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes wave {
          from { transform: rotate(-10deg); }
          to   { transform: rotate(14deg); }
        }
      `}</style>
    </div>
  );
}

/* ─────────── Helper components ─────────── */

function DashboardPreview() {
  return (
    <div className="floating-preview w-[300px] rounded-2xl overflow-hidden relative z-10" style={{ background: "#FFFFFF" }}>
      {/* Mini topbar */}
      <div className="flex items-center gap-1.5 p-2.5 border-b border-slate-100">
        <div className="h-2 w-2 rounded-full bg-rose-400" />
        <div className="h-2 w-2 rounded-full bg-amber-400" />
        <div className="h-2 w-2 rounded-full bg-emerald-400" />
        <div className="ms-2 h-3 flex-1 rounded bg-slate-100" />
      </div>
      {/* Mini stat cards */}
      <div className="p-3 space-y-2">
        <div className="grid grid-cols-3 gap-1.5">
          {[
            { label: "طلاب", val: "248", color: "#0E7469" },
            { label: "مواد", val: "14", color: "#0284C7" },
            { label: "شهادات", val: "196", color: "#DAA520" },
          ].map((s, i) => (
            <div key={i} className="rounded-lg p-2" style={{ background: "#FAFCFB", border: "1px solid #EEF3EF" }}>
              <div className="text-[7px] text-slate-500 font-medium mb-0.5">{s.label}</div>
              <div className="font-display text-[13px] font-bold tabular-nums" style={{ color: s.color }}>{s.val}</div>
            </div>
          ))}
        </div>
        {/* Mini chart */}
        <div className="rounded-lg p-2.5" style={{ background: "#FAFCFB", border: "1px solid #EEF3EF" }}>
          <div className="text-[7px] text-slate-500 font-medium mb-2">توزيع الدرجات</div>
          <div className="flex items-end gap-1 h-16">
            {[70, 45, 90, 55, 80, 60, 75].map((h, i) => (
              <div
                key={i}
                className="flex-1 rounded-sm"
                style={{
                  height: `${h}%`,
                  background: `linear-gradient(180deg, #14B8A6, #0E7469)`,
                }}
              />
            ))}
          </div>
        </div>
        {/* Mini list */}
        <div className="space-y-1">
          {[
            { name: "أحمد محمد", grade: "أ+", color: "#0E7469" },
            { name: "فاطمة علي", grade: "أ", color: "#0284C7" },
            { name: "يوسف كامل", grade: "أ+", color: "#0E7469" },
          ].map((r, i) => (
            <div key={i} className="flex items-center justify-between p-1.5 rounded-md" style={{ background: i === 0 ? "#F0FAF7" : "transparent" }}>
              <div className="flex items-center gap-1.5">
                <div className="h-4 w-4 rounded-full" style={{ background: "linear-gradient(135deg, #0E7469, #14B8A6)" }} />
                <span className="text-[8px] font-medium text-slate-700">{r.name}</span>
              </div>
              <span className="text-[8px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: `${r.color}18`, color: r.color }}>
                {r.grade}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function BulletPoint({ text }) {
  return (
    <div className="flex items-center gap-2.5 text-[13px] text-white/85">
      <div className="h-5 w-5 rounded-md flex items-center justify-center shrink-0"
           style={{ background: "rgba(244, 208, 111, 0.16)", border: "1px solid rgba(244, 208, 111, 0.30)" }}>
        <CheckCircle2 className="h-3 w-3" style={{ color: "#F4D06F" }} strokeWidth={2.5} />
      </div>
      <span>{text}</span>
    </div>
  );
}

function BigStat({ value, label }) {
  return (
    <div>
      <div className="font-display text-[28px] xl:text-[32px] font-bold text-white leading-none tracking-tight tabular-nums">
        {value}
      </div>
      <div className="text-[11px] text-white/55 mt-2">{label}</div>
    </div>
  );
}
